import { Loader2 } from 'lucide-react';
import { useAdminStats, useAdminScrapingSummary } from '../../hooks/useAdminApi';

export function AdminOverviewPage() {
  const stats = useAdminStats();
  const summary = useAdminScrapingSummary();

  if (stats.isLoading || summary.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#8b949e] animate-spin" />
      </div>
    );
  }

  const s = stats.data;
  const sc = summary.data;

  const coveragePct = s ? s.scrapeCoverage24h * 100 : 0;
  const errorRatePct = s ? s.errorRate24h * 100 : 0;

  // Derive totals from phaseBreakdown
  const phases = sc?.phaseBreakdown ?? [];
  const totalSucceeded = phases.reduce((sum, p) => sum + p.succeeded, 0);
  const totalFailed = phases.reduce((sum, p) => sum + p.failed, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-white">Overview</h1>
        <p className="text-[13px] text-[#8b949e]">At-a-glance health across all systems</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">TOTAL FACILITIES</div>
          <div className="text-2xl font-bold text-white">{s?.totalFacilities ?? 0}</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">24H COVERAGE</div>
          <div className="text-2xl font-bold text-emerald-500">{coveragePct.toFixed(0)}%</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">facilities scraped today</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">ERROR RATE</div>
          <div className="text-2xl font-bold text-red-500">{errorRatePct.toFixed(1)}%</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">failed scrapes 24h</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">ACTIVE ALERTS</div>
          <div className="text-2xl font-bold text-white">{s?.activeAlerts ?? 0}</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">active requests</div>
        </div>
      </div>

      {/* Scraping summary bar */}
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4 mb-5">
        <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">SCRAPING OVERVIEW (1H)</div>
        <div className="flex items-end gap-1 h-[120px]">
          {(() => {
            const bars = phases.map((p) => ({
              label: p.source,
              success: p.succeeded,
              failed: p.failed,
            }));
            // Add totals bar
            bars.push(
              { label: 'Success', success: totalSucceeded, failed: 0 },
              { label: 'Errors', success: 0, failed: totalFailed },
            );
            const max = Math.max(...bars.map(b => b.success + b.failed), 1);
            return bars.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex-1 flex flex-col justify-end gap-px w-full">
                  {d.success > 0 && (
                    <div
                      className="bg-emerald-500 rounded-t w-full"
                      style={{ height: `${(d.success / max) * 100}%` }}
                    />
                  )}
                  {d.failed > 0 && (
                    <div
                      className="bg-red-500 rounded-t w-full"
                      style={{ height: `${(d.failed / max) * 100}%` }}
                    />
                  )}
                </div>
                <div className="text-[10px] text-[#8b949e] mt-1">{d.label}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Two-column panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">NOTIFICATIONS</div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Sent in 24h</span>
              <span className="text-[13px] font-medium text-emerald-500">{s?.notificationsSent24h ?? 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">SUBSCRIPTIONS</div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Pro</span>
              <span className="text-[13px] font-medium text-emerald-500">{s?.subscriptions?.pro ?? 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-[#8b949e]">Free</span>
              <span className="text-[13px] font-medium text-white">{s?.subscriptions?.free ?? 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
